import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { SnackbarAlert } from "../components/pages/common/styled";

const NotificationContext = createContext(null);
const PENDING_TOAST_KEY = "app:pending-toast";

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    open: false,
    severity: "info",
    title: "",
    message: "",
  });

  const showNotification = useCallback((severity, message, title = "") => {
    setNotification({
      open: true,
      severity,
      title,
      message,
    });
  }, []);

  const showSuccess = useCallback((message, title = "Success") => {
    showNotification("success", message, title);
  }, [showNotification]);

  const showError = useCallback((message, title = "Error") => {
    showNotification("error", message, title);
  }, [showNotification]);

  const showWarning = useCallback((message, title = "Warning") => {
    showNotification("warning", message, title);
  }, [showNotification]);

  const showInfo = useCallback((message, title = "Info") => {
    showNotification("info", message, title);
  }, [showNotification]);

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    try {
      const pendingToastRaw = window.sessionStorage.getItem(PENDING_TOAST_KEY);
      if (pendingToastRaw) {
        const pendingToast = JSON.parse(pendingToastRaw);
        showNotification(pendingToast.severity || "error", pendingToast.message || "Something went wrong", pendingToast.title || "Error");
        window.sessionStorage.removeItem(PENDING_TOAST_KEY);
      }
    } catch (err) {
      window.sessionStorage.removeItem(PENDING_TOAST_KEY);
    }

    const handleGlobalToast = (event) => {
      const detail = event?.detail || {};
      showNotification(detail.severity || "error", detail.message || "Something went wrong", detail.title || "Error");
    };

    window.addEventListener("app:toast", handleGlobalToast);
    return () => window.removeEventListener("app:toast", handleGlobalToast);
  }, [showNotification]);

  const value = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <SnackbarAlert
        open={notification.open}
        onClose={hideNotification}
        severity={notification.severity}
        title={notification.title}
        message={notification.message}
      />
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
